import React, { useMemo } from 'react';
import { Polyline } from '@react-google-maps/api';

// Color palette matching MapMarkers
const PERSON_COLORS = [
  '#1565c0', // Deep blue
  '#b71c1c', // Deep crimson
  '#2e7d32', // Forest green
  '#bf8040', // Muted bronze
  '#6a1b9a', // Deep purple
  '#00838f', // Deep cyan
  '#f57f17', // Deep amber
  '#c2185b', // Deep magenta
  '#4e342e', // Dark brown
  '#455a64', // Dark blue grey
];

/**
 * MovementTrail - Render polyline connecting locations within time threshold
 */
export default function MovementTrail({ events, maxTimeGapMinutes, filterPersonName }) {
  // Build person map for color coding
  const personMap = useMemo(() => {
    const uniquePersons = new Map();
    events.forEach(event => {
      const rawName = event.metadata?.personName || event.userId || 'Unknown';
      const eventPersonName = rawName.trim();
      if (!uniquePersons.has(eventPersonName)) {
        uniquePersons.set(eventPersonName, uniquePersons.size);
      }
    });
    return uniquePersons;
  }, [events]);

  if (events.length <= 1) return null;

  return (
    <>
      {events.map((event, index) => {
        if (index === events.length - 1) return null;

        const nextEvent = events[index + 1];
        const eventPersonName = (event.metadata?.personName || event.userId || 'Unknown').trim();
        const nextPersonName = (nextEvent.metadata?.personName || nextEvent.userId || 'Unknown').trim();

        // Skip if filtering by person and this event doesn't match
        if (filterPersonName && eventPersonName !== filterPersonName) return null;

        // Only connect events from the same person
        if (eventPersonName !== nextPersonName) return null;

        const timeDiff = Math.abs(
          new Date(nextEvent.timestamp || nextEvent.createdAt).getTime() -
          new Date(event.timestamp || event.createdAt).getTime()
        ) / 1000; // Convert to seconds

        // Only draw line if time difference is less than maxTimeGapMinutes
        if (timeDiff >= maxTimeGapMinutes * 60) return null;

        // Get color for this person
        const personIndex = personMap.get(eventPersonName) || 0;
        const strokeColor = PERSON_COLORS[personIndex % PERSON_COLORS.length];

        return (
          <Polyline
            key={`line-${index}`}
            path={[
              { lat: event.location.latitude, lng: event.location.longitude },
              { lat: nextEvent.location.latitude, lng: nextEvent.location.longitude }
            ]}
            options={{
              strokeColor: strokeColor,
              strokeWeight: 3,
              strokeOpacity: 0.7,
              icons: [{
                icon: {
                  path: 'M 0,-1 0,1',
                  strokeOpacity: 1,
                  scale: 3
                },
                offset: '0',
                repeat: '10px'
              }]
            }}
          />
        );
      })}
    </>
  );
}
